/**
 * @file 表单校验规则对象
 * @description 用于表单中各个字段的校验规则。这些规则通常会在表单提交或字段失焦时触发，以确保用户输入的数据符合要求。
 */

/**
 * 表单校验规则对象
 * @typedef {Object} FormRules
 * @property {Array} [字段名] - 字段的校验规则数组
 * @property {Object} [字段名] - 字段的校验规则对象
 */

import { decimalValidate, hzValidate, currentValueValidate, voltageValueValidate } from '@/utils/tableValidation.js'

// 映射表表单校验规则对象
export const mappingFormRules = {
  mappingName: [
    { required: true, message: '请输入映射表名称', trigger: 'blur' }
  ],
  mappingContent: [
    { required: true, message: '请输入映射表内容', trigger: 'blur' }
  ],
  agreementType: [
    { required: true, message: '请选择协议类型', trigger: 'change' }
  ],
  mappingType: [
    { required: true, message: '请选择映射表类型', trigger: 'blur' }
  ]
}

// 映射键值对校验规则
export const mappingRule = {
  key: { required: true, message: '请输入映射值' },
  value: { required: true, message: '请输入映射内容' }
}

// bit位映射表校验规则
export const bitTableRules = {
  startingBit: [{ required: true, message: '请输入起始位', trigger: 'blur' }],
  bitLength: [{ required: true, message: '请输入位长度', trigger: 'blur' }],
  name: [{ required: true, message: '请输入映射内容', trigger: 'blur' }]
}

// 协议表单校验规则
export const protocolFormRules = {
  protocolName: [
    { required: true, message: '请输入协议名称', trigger: 'blur' }
  ],
  protocolType: [
    { required: true, message: '请选择协议类型', trigger: 'blur' }
  ],
  deviceType: [
    { required: true, message: '请选择设备类型', trigger: 'blur' }
  ],
  protocolVersion: [
    { required: true, message: '请选择协议版本', trigger: 'blur' }
  ]
}

// 协议表格校验规则
export const protocolTableRules = {
  startAddress: [
    { required: true, message: '请输入起始地址', trigger: 'blur' }
  ],
  subIndex: [
    { required: true, message: '请输入子索引', trigger: 'blur' },
    { 
      validator: (rule, value, callback) => {
        // 十六进制正则：仅允许 0-9、a-f、A-F
        const hexReg = /^[0-9a-fA-F]+$/;
        if (!value) {
          return callback(new Error('请输入子索引'));
        }
        if (!hexReg.test(value)) {
          return callback(new Error('请输入有效的十六进制值'));
        }
          callback();
        },
      trigger: 'blur'
    }
  ],
  dataName: [
    { required: true, message: '请输入数据名称', trigger: 'blur' }
  ],
  dataLength: [
    { required: true, message: '请输入数据长度', trigger: 'blur' }
  ],
  dataType: [
    { required: true, message: '请选择数据类型', trigger: 'blur' }
  ],
  decimal: [
    { required: true, trigger: 'blur', validator: decimalValidate }
  ]
}

// 测试项表单校验规则
export const testItemFormRules = {
  testItemName: [
    { required: true, message: '请输入测试项名称', trigger: 'blur' }
  ],
  testItemDescription: [
    { required: true, message: '请输入测试项描述', trigger: 'blur' }
  ],
  protocolType: [
    { required: true, message: '请选择协议类型', trigger: 'blur' }
  ],
  deviceType: [
    { required: true, message: '请选择设备类型', trigger: 'blur' }
  ],
  protocolName: [
    { required: true, message: '请选择协议', trigger: 'blur' }
  ]
}

// 测试项表格校验规则
export const testItemTableRules = {
  operation: [
    { required: true, message: '请选择测试操作', trigger: 'blur' }
  ],
  // 测试项参数校验规则，根据不同的测试操作类型进行不同的校验
  param: {
    threePhaseSource: {
      currentPhase: [{ required: true, message: '请选择电流相别', trigger: 'blur' }],
      currentPhaseValue: [{ required: true, validator: currentValueValidate, trigger: 'blur' }],
      voltagePhase: [{ required: true, message: '请选择电压相别', trigger: 'blur' }],
      voltagePhaseValue: [{ required: true, validator: voltageValueValidate, trigger: 'blur' }],
      powerFactor: [{ required: true, message: '请选择功率因数', trigger: 'blur' }],
      hz: [{ required: true, trigger: 'blur', validator: hzValidate }]
    },
    signalGenerator: {
      phase: [{ required: true, message: '请选择相别', trigger: 'blur' }],
      amplitude: [{ required: true, message: '请输入幅值', trigger: 'blur' }],
      signalType: [{ required: true, message: '请选择波形', trigger: 'blur' }],
      tRatio: [{ required: true, message: '请输入互感器变比', trigger: 'blur' }],
      hz: [{ required: true, trigger: 'blur', validator: hzValidate }]
    },
    countUp: {
      time: [{ required: true, message: '请输入计数时间', trigger: 'blur' }],
      unit: [{ required: true, message: '请选择单位', trigger: 'blur' }]
    },
    writeData: {
      wContent: [{ required: true, message: '请输入写入内容', trigger: 'blur' }],
      wAddress: [{ required: true, message: '请选择写入地址', trigger: 'blur' }]
    },
    readData: {
      rAddress: [{ required: true, message: '请选择对应地址', trigger: 'change' }],
      cMode: [{ required: true, message: '请选择对比方式', trigger: 'change' }],
      rContent: [{ required: true, message: '请输入对比内容', trigger: 'change' }],
      min: [{ required: true, message: '请输入对比内容最小值', trigger: 'change' }],
      max: [{ required: true, message: '请输入对比内容最大值', trigger: 'change' }]
      // min: [{ required: true, validator: rangeValidate }],
    },
    readDataMinMax: {
      rAddress: [{ required: true, message: '请选择对应地址', trigger: 'change' }],
      cMode: [{ required: true, message: '请选择对比方式', trigger: 'change' }],
      min: [{ required: true, message: '请输入对比内容最小值', trigger: 'change' }],
      max: [{ required: true, message: '请输入对比内容最大值', trigger: 'change' }]
    }
  }
}

// 测试用例表单校验规则
export const testCaseFormRules = {
  testCaseName: [
    { required: true, message: '请输入测试用例名称', trigger: 'blur' }
  ],
  testCaseDescription: [
    { required: true, message: '请输入测试用例描述', trigger: 'blur' }
  ],
  protocolType: [
    { required: true, message: '请选择协议类型', trigger: 'blur' }
  ],
  deviceType: [
    { required: true, message: '请选择设备类型', trigger: 'blur' }
  ],
  protocolName: [
    { required: true, message: '请选择协议', trigger: 'blur' }
  ]
}

// 测试用例表格校验规则
export const testCaseTableRules = {
  testItemName: [
    { required: true, message: '请选择测试项名称', trigger: 'blur' }
  ],
  delayTime: [
    { required: true, message: '请输入延迟时间', trigger: 'blur' }
  ],
  testTimes: [
    { required: true, message: '请输入测试次数', trigger: 'blur' }
  ]
}

// 端口配置表单校验规则
export const portConfigFormRules = {
  portName: [
    { required: true, message: '请选择串口', trigger: 'blur' }
  ],
  baudRate: [
    { required: true, message: '请输入波特率', trigger: 'blur' }
  ],
  dataBits: [
    { required: true, message: '请输入数据位', trigger: 'blur' }
  ],
  stopBits: [
    { required: true, message: '请输入停止位', trigger: 'blur' }
  ],
  parity: [
    { required: true, message: '请选择校验位', trigger: 'blur' }
  ],
  timeout: [
    { required: true, message: '请输入超时时间', trigger: 'blur' }
  ],
  slaveId: [
    { required: true, message: '请输入十六进制字符串', trigger: 'blur' }
  ]
}

// 网络端口配置表格校验规则
export const netPortConfigTableRules = {
  ipAddress: [
    { required: true, message: '请输入IP地址', trigger: 'blur' }
  ]
}
